/**
 * Settings → People: who can sign in, and what each of them can do.
 *
 * The owner edits; the office and supervisors can see the list. Three things
 * the screen makes plain because the server enforces them (CORE-2, D53):
 * a change of role or switching someone off takes effect on their very next
 * request; a new account's password is temporary and shown once; and the last
 * owner cannot be demoted or switched off.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, writeToken } from "../lib/api";
import { money, titleCase } from "../lib/format";
import type { PersonOut, PersonWithPassword, Role } from "../lib/types";
import { Card, Chip } from "./ui";

const ROLES: Role[] = ["owner", "office", "supervisor", "technician", "subcontractor", "read_only"];

export function PeopleSettings({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const people = useQuery({ queryKey: ["people"], queryFn: () => api.people.list() });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api.people.roles() });
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const [handover, setHandover] = useState<PersonWithPassword | null>(null);
  const [adding, setAdding] = useState(false);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["people"] });
  const fail = (err: unknown) =>
    setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Something went wrong." });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.people.update>[1] }) =>
      api.people.update(id, body),
    onSuccess: (p) => {
      refresh();
      setSaid({ tone: "info", text: `${p.full_name} updated. If their access changed, they're signed out now and sign in again with the new level.` });
    },
    onError: fail,
  });
  const reset = useMutation({
    mutationFn: (id: string) => api.people.resetPassword(id),
    onSuccess: (r) => {
      refresh();
      setHandover(r);
    },
    onError: fail,
  });

  if (people.error) {
    return (
      <Card title="People">
        <p className="tiny muted">The staff list is for the owner and office.</p>
      </Card>
    );
  }

  return (
    <Card title="People" subtitle="Who can sign in, and what they can do">
      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
          {said.text}
        </p>
      )}
      {handover && <PasswordHandover result={handover} onDone={() => setHandover(null)} />}

      <div className="people">
        {(people.data ?? []).map((p) => (
          <PersonRow
            key={p.id}
            person={p}
            isOwner={isOwner}
            onRole={(role) => update.mutate({ id: p.id, body: { role } })}
            onActive={(is_active) => update.mutate({ id: p.id, body: { is_active } })}
            onReset={() => {
              if (window.confirm(`Give ${p.full_name} a new temporary password? They'll be signed out everywhere.`)) {
                reset.mutate(p.id);
              }
            }}
          />
        ))}
      </div>

      {isOwner &&
        (adding ? (
          <AddPerson
            onCancel={() => setAdding(false)}
            onAdded={(r) => {
              setAdding(false);
              refresh();
              setHandover(r);
            }}
            onError={fail}
          />
        ) : (
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className="btn btn-primary" type="button" onClick={() => setAdding(true)}>
              Add a person
            </button>
          </div>
        ))}

      {roles.data && (
        <details style={{ marginTop: 12 }}>
          <summary className="tiny muted">What each permission level can do</summary>
          <dl className="kv tiny" style={{ marginTop: 8 }}>
            {roles.data.map((r) => (
              <div key={r.role} style={{ display: "contents" }}>
                <dt>{titleCase(r.role.replace("_", " "))}</dt>
                <dd>{r.means}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </Card>
  );
}

function PersonRow({
  person,
  isOwner,
  onRole,
  onActive,
  onReset,
}: {
  person: PersonOut;
  isOwner: boolean;
  onRole: (role: Role) => void;
  onActive: (active: boolean) => void;
  onReset: () => void;
}) {
  return (
    <div className={`person${person.is_active ? "" : " person-off"}`}>
      <div className="person-who">
        <b>{person.full_name}</b>
        {person.is_you && <Chip tone="info">you</Chip>}
        {!person.is_active && <Chip tone="neutral">switched off</Chip>}
        {person.must_change_password && person.is_active && <Chip tone="warn">temporary password</Chip>}
        <div className="tiny muted">
          {person.email}
          {person.phone ? ` · ${person.phone}` : ""}
          {isOwner && person.hourly_charge_cents > 0 ? ` · charges ${money(person.hourly_charge_cents)}/hr` : ""}
        </div>
      </div>
      <div className="person-controls">
        {isOwner ? (
          <select
            aria-label={`Permission level for ${person.full_name}`}
            value={person.role}
            onChange={(e) => onRole(e.target.value as Role)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {titleCase(r.replace("_", " "))}
              </option>
            ))}
          </select>
        ) : (
          <Chip tone="neutral">{titleCase(person.role.replace("_", " "))}</Chip>
        )}
        {isOwner && !person.is_you && (
          <>
            <button className="linkish tiny" type="button" onClick={() => onActive(!person.is_active)}>
              {person.is_active ? "Switch off" : "Switch on"}
            </button>
            {person.is_active && (
              <button className="linkish tiny" type="button" onClick={onReset}>
                Reset password
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AddPerson({
  onAdded,
  onCancel,
  onError,
}: {
  onAdded: (r: PersonWithPassword) => void;
  onCancel: () => void;
  onError: (err: unknown) => void;
}) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", role: "technician" as Role });
  const add = useMutation({
    mutationFn: () =>
      api.people.add({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        role: form.role,
        is_field: ["technician", "subcontractor", "supervisor", "owner"].includes(form.role),
      }),
    onSuccess: onAdded,
    onError,
  });

  return (
    <div className="add-person">
      <div className="form-grid">
        <label className="field">
          <span>Name</span>
          <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </label>
        <label className="field">
          <span>Email — what they sign in with</span>
          <input
            type="email"
            autoCapitalize="none"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Mobile</span>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <label className="field">
          <span>Permission level</span>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {titleCase(r.replace("_", " "))}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="btn-row" style={{ marginTop: 10 }}>
        <button
          className="btn btn-primary"
          type="button"
          disabled={add.isPending || !form.full_name.trim() || !form.email.trim()}
          onClick={() => add.mutate()}
        >
          {add.isPending ? "Adding…" : "Add"}
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The one time a temporary password is shown. */
function PasswordHandover({ result, onDone }: { result: PersonWithPassword; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="notice notice-info" role="status">
      <p>
        <b>{result.user.full_name}</b> signs in with <b>{result.user.email}</b> and this temporary password. It
        won't be shown again — they'll choose their own the first time they sign in.
      </p>
      <p className="mono" style={{ fontSize: 20, margin: "8px 0", letterSpacing: "0.04em" }}>
        {result.temporary_password}
      </p>
      <div className="btn-row">
        <button
          className="btn"
          type="button"
          onClick={() =>
            // navigator.clipboard is undefined over plain http; the password
            // is on screen either way, so a failed copy just leaves the label.
            void Promise.resolve()
              .then(() => navigator.clipboard.writeText(result.temporary_password))
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

export function ChangeOwnPassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const change = useMutation({
    mutationFn: () => api.auth.changePassword(current, next),
    onSuccess: (token) => {
      // Other sessions end; this one carries on with the fresh token.
      writeToken(token.access_token);
      setCurrent("");
      setNext("");
      setSaid({ tone: "info", text: "Changed. You've been signed out on your other devices." });
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't change it." }),
  });
  return (
    <details style={{ marginTop: 12 }}>
      <summary className="tiny">Change your password</summary>
      <div className="form-grid" style={{ marginTop: 8 }}>
        <label className="field">
          <span>Current password</span>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </label>
        <label className="field">
          <span>New password (10+)</span>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </label>
      </div>
      {said && (
        <p className={`notice notice-${said.tone}`} style={{ marginTop: 8 }}>
          {said.text}
        </p>
      )}
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button className="btn" type="button" disabled={!current || next.length < 10 || change.isPending} onClick={() => change.mutate()}>
          {change.isPending ? "Saving…" : "Change password"}
        </button>
      </div>
    </details>
  );
}
